import {useQueryClient} from '@tanstack/react-query';
import {pulseUserAtom} from 'atoms/auth';
import {loggedInEmployeeAtom} from 'atoms/employee';
import type {Visit} from 'client';
import {useCollabJWTToken} from 'hooks/useJWTToken';
import {usePresenceAwareness, User} from 'hooks/usePresenceAwareness';
import {atom, useAtomValue, useSetAtom, useStore} from 'jotai';
import {useCallback, useEffect, useMemo, useRef} from 'react';
import {useLocation} from 'react-router';

import {usePresencePageKey} from './useRecordVisit';

// Pages that need faster presence updates
const FAST_PRESENCE_PATHS = ['/workstreams', '/planning'];

// Single atom for all presence data - much simpler!
export const presenceAtom = atom<{
  users: User[];
  lastUpdated: number;
  isConnected: boolean;
}>({
  users: [],
  lastUpdated: 0,
  isConnected: false,
});

const dedupePresenceUsers = (users: User[]) => {
  const userMap = new Map<string, User>();

  users.forEach((currentUser) => {
    const currentKey = `${currentUser.ldap}-${currentUser.pageKey || 'unknown'}`;
    userMap.set(currentKey, currentUser);
  });

  return Array.from(userMap.values());
};

export const usePresenceTracking = () => {
  const location = useLocation();
  const pageKey = usePresencePageKey();
  const queryClient = useQueryClient();
  const store = useStore();
  const setPresence = useSetAtom(presenceAtom);
  const lastUpdateRef = useRef<number>(0);
  const throttleTimeoutRef = useRef<NodeJS.Timeout>();
  const hasConnectedRef = useRef(false);
  const isConnectedRef = useRef(false);

  // Fetch JWT token directly (React Query handles caching)
  const {token: rawToken} = useCollabJWTToken();
  const authUser = useAtomValue(pulseUserAtom);
  const {employee} = useAtomValue(loggedInEmployeeAtom);

  // Ensure token is a string (API might return object or string)
  const authToken = useMemo(() => {
    if (!rawToken) return undefined;
    return typeof rawToken === 'string' ? rawToken : (rawToken as any)?.token || String(rawToken);
  }, [rawToken]);

  // Memoize user object to prevent infinite re-renders
  const user: User | undefined = useMemo(() => {
    return employee
      ? {
          id: employee.ldap,
          ldap: employee.ldap,
          name: employee.name,
          role: employee.role ?? undefined,
          level: employee.level ?? undefined,
          avatar: employee.profile_photo_url ?? undefined,
        }
      : undefined;
  }, [employee]);

  const updatePresenceUsers = useCallback(
    (users: User[], isConnected: boolean) => {
      const now = Date.now();

      // Determine throttle delay based on current page
      const currentPath = location.pathname;
      const isFastPath = FAST_PRESENCE_PATHS.some((path) => currentPath.startsWith(path));
      const throttleDelay = isFastPath ? 500 : 2000; // 500ms for fast paths, 2s for others

      const uniqueUsers = dedupePresenceUsers(users);

      const commitPresence = () => {
        const lastUpdated = Date.now();
        const leftAt = new Date(lastUpdated).toISOString().replace(/Z$/, '');
        const nextUserKeys = new Set(
          uniqueUsers.filter((u) => u.pageKey).map((u) => `${u.pageKey}:${u.ldap}`)
        );

        store.get(presenceAtom).users.forEach((previousUser) => {
          if (
            !previousUser.pageKey ||
            nextUserKeys.has(`${previousUser.pageKey}:${previousUser.ldap}`)
          ) {
            return;
          }

          // The visits API timestamp can lag behind live presence, so patch the cached visitor
          // row when awareness tells us exactly when someone left this page.
          queryClient.setQueryData<Visit[]>(['presence', previousUser.pageKey], (visitors) =>
            visitors?.map((visitor) =>
              visitor.employee.ldap === previousUser.ldap
                ? {
                    ...visitor,
                    last_seen: leftAt,
                  }
                : visitor
            )
          );
        });

        setPresence({
          users: uniqueUsers,
          lastUpdated,
          isConnected,
        });
        lastUpdateRef.current = lastUpdated;
        throttleTimeoutRef.current = undefined;
      };

      const elapsed = now - lastUpdateRef.current;
      if (lastUpdateRef.current === 0 || elapsed >= throttleDelay) {
        if (throttleTimeoutRef.current) {
          clearTimeout(throttleTimeoutRef.current);
          throttleTimeoutRef.current = undefined;
        }
        commitPresence();
        return;
      }

      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
      }

      throttleTimeoutRef.current = setTimeout(commitPresence, throttleDelay - elapsed);
    },
    [queryClient, setPresence, store, location.pathname]
  );

  useEffect(() => {
    lastUpdateRef.current = 0;
    if (throttleTimeoutRef.current) {
      clearTimeout(throttleTimeoutRef.current);
      throttleTimeoutRef.current = undefined;
    }
  }, [pageKey]);

  const {isConnected, error} = usePresenceAwareness({
    token: !authToken ? undefined : authToken,
    pageKey,
    user,
    onUsersChange: (users: User[]) => updatePresenceUsers(users, isConnectedRef.current),
  });

  // Update connection status
  useEffect(() => {
    if (isConnected === undefined) {
      return;
    }

    isConnectedRef.current = isConnected;

    if (isConnected) {
      hasConnectedRef.current = true;
      setPresence((currentPresence) => ({
        ...currentPresence,
        lastUpdated: Date.now(),
        isConnected: true,
      }));
      return;
    }

    if (!hasConnectedRef.current) {
      return;
    }

    if (throttleTimeoutRef.current) {
      clearTimeout(throttleTimeoutRef.current);
      throttleTimeoutRef.current = undefined;
    }

    lastUpdateRef.current = 0;
    setPresence({
      users: [],
      lastUpdated: Date.now(),
      isConnected: false,
    });
  }, [isConnected, setPresence]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
      }
    };
  }, []);

  return {
    isConnected,
    error,
    hasAuth: !!authUser && !!authToken,
  };
};
